<template>
  <div>
    <v-row v-show="!hideName">
      <v-col>
        <v-card class="elevation-0" min-width="200">
          <v-card-title class="pt-0 pl-0"> Environment </v-card-title>
          <v-card-text class="pb-0 pl-0">
            <v-col class="pa-0" cols="6">
              <v-text-field
                v-bind="bind('environment_name')"
                label="Name"
                hint="Set the name of your StackAssist environment."
                class="required"
                @input="updateValue('environment_name', $event)"
              />
            </v-col>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Default options -->
    <v-row>
      <v-col>
        <v-card class="elevation-0" min-width="200">
          <v-card-title class="pl-0"> Default options </v-card-title>
          <v-card-text class="pl-0">
            <v-autocomplete
              v-show="!hideProductCategory"
              label="Default product category"
              hint="Set the default product category for maintenance of product data."
              class="required"
              item-text="name"
              item-value="id"
              v-bind="bind('default_product_category_id')"
              :items="categories"
              @input="updateValue('default_product_category_id', $event)"
            />
            <v-autocomplete
              label="Packaging selection rule"
              hint="Set the default packaging selection rule for the create order process."
              class="required"
              item-text="label"
              item-value="value"
              :items="packagingSelectionRules"
              v-bind="bind('default_load')"
              @input="updateValue('default_load', $event)"
            />
            <v-select
              label="Default quantity type"
              hint="Set the default quantity type for the create order process."
              class="required"
              item-text="label"
              item-value="value"
              :items="quantityTypes"
              v-bind="bind('default_quantity_type')"
              @input="updateValue('default_quantity_type', $event)"
            />
            <v-select
              label="Default packaging type"
              hint="The packaging type which will be automatically selected when creating an order."
              class="required"
              :items="packagingTypes"
              item-value="value"
              item-text="label"
              :value="defaultPackagingType"
              name="default_packaging_type"
              :error-messages="errors['default_packaging_type']"
              @input="defaultPackagingType = $event"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Default load settings -->
      <v-col v-show="!hideDefaultLoadSettings">
        <v-card class="elevation-0" min-width="200">
          <v-card-title>Default load settings</v-card-title>
          <v-card-text>
            <LoadSettingInput
              label="Default box"
              hint="Set the default load setting for the box"
              scope="box"
              v-bind="bind('default_box_id')"
              :environment-override="environmentOverride"
              @input="updateValue('default_box_id', $event)"
            />
            <LoadSettingInput
              label="Default pallet"
              hint="Set the default load setting for the pallet"
              scope="pallet"
              v-bind="bind('default_pallet_id')"
              :environment-override="environmentOverride"
              :default-value="value.default_pallet_id"
              @input="updateValue('default_pallet_id', $event)"
            />
            <LoadSettingInput
              label="Default container"
              hint="Set the default load setting for the container"
              scope="container"
              v-bind="bind('default_container_id')"
              :environment-override="environmentOverride"
              @input="updateValue('default_container_id', $event)"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Weight unit -->
      <v-col>
        <v-card class="elevation-0 pb-0" min-width="200">
          <v-card-title class="required pb-0"> Weight unit </v-card-title>
          <v-card-text>
            <v-radio-group
              mandatory
              v-bind="bind('weight_unit')"
              @change="updateValue('weight_unit', $event)"
            >
              <v-radio
                v-for="weightUnit in weightUnits"
                :key="weightUnit.value"
                :label="weightUnit.label"
                :value="weightUnit.value"
              />
            </v-radio-group>
          </v-card-text>
          <v-card-title class="required pb-0 pt-0"> Size unit </v-card-title>
          <v-card-text>
            <v-radio-group
              mandatory
              v-bind="bind('size_unit')"
              @change="updateValue('size_unit', $event)"
            >
              <v-radio
                v-for="sizeUnit in sizeUnits"
                :key="sizeUnit.value"
                :label="sizeUnit.label"
                :value="sizeUnit.value"
              />
            </v-radio-group>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>
<script>
import LoadSettingInput from '@/components/inputs/LoadSettingInput.vue'
import { SIZE_UNITS } from '@/constants/units/sizeUnits'
import { WEIGHT_UNITS } from '@/constants/units/weightUnits'
import {
  PACKAGING_TYPE_LABELS,
  PACKAGING_TYPES_LIST,
} from '@/constants/packagingTypes'

export default {
  name: 'GlobalSettingsForm',
  components: { LoadSettingInput },
  props: [
    'value',
    'errors',
    'categories',
    'packagingSelectionRules',
    'quantityTypes',
    'hideDefaultLoadSettings',
    'hideName',
    'hideProductCategory',
    'environmentOverride',
  ],

  computed: {
    weightUnits() {
      return WEIGHT_UNITS
    },
    sizeUnits() {
      return SIZE_UNITS
    },
    packagingTypes() {
      const list = PACKAGING_TYPES_LIST.map((type) => ({
        label: PACKAGING_TYPE_LABELS[type],
        value: type,
      }))
      list.unshift({ label: 'All packaging', value: 'all' })

      return list
    },
    defaultPackagingType: {
      get() {
        return this.value.default_packaging_type ?? 'all'
      },
      set(defaultPackagingType) {
        if (defaultPackagingType === 'all') {
          defaultPackagingType = null
        }

        this.value.default_packaging_type = defaultPackagingType

        this.$emit('clearError', 'default_packaging_type')
        this.$emit('input', {
          ...this.value,
          default_packaging_type: defaultPackagingType,
        })
      },
    },
  },

  methods: {
    bind(name) {
      return {
        name,
        value: this.value[name],
        errorMessages: this.errors?.[name] ?? [],
      }
    },

    updateValue(field, value) {
      this.$emit('clearError', field)
      this.$emit('input', {
        ...this.value,
        [field]: value,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.disabled {
  opacity: 0.5;
}
</style>
